// resolveStageBadgeData — ID→name lookups for the deal-stage badge and
// "scored by" caption on the analysis surface (Phase B, B-7).
//
// Extracted out of RecordDetailInline (the caller) so this lookup — not the
// whole 1500+ line panel — can be unit tested directly. Pure and
// presentational-adjacent: StageBadge itself never sees raw ids, so an
// absent/stale id or an unavailable reference (embedded mode, no CRM
// connection) must resolve to `null` here rather than leaking an id through.

import type { CrmStageNamesReference } from '@aica/api-types';
import type { PromptProfile } from '@/lib/api/types';

export interface ResolveStageBadgeDataInput {
  crmPipelineId?: string | null;
  crmStageId?: string | null;
  promptProfileId?: string | null;
  promptProfiles: PromptProfile[];
  /** The pipelines of the record's own CRM install (`GET /crm/stage-names`). */
  crmStagesReference: CrmStageNamesReference['pipelines'];
}

export interface ResolveStageBadgeDataResult {
  promptProfileName: string | null;
  crmStageName: string | null;
  crmPipelineName: string | null;
  /** Org has more than one CRM pipeline — StageBadge only disambiguates then. */
  crmMultiplePipelines: boolean;
}

export function resolveStageBadgeData({
  crmPipelineId,
  crmStageId,
  promptProfileId,
  promptProfiles,
  crmStagesReference,
}: ResolveStageBadgeDataInput): ResolveStageBadgeDataResult {
  const promptProfileName = promptProfileId
    ? (promptProfiles.find((p) => p.id === promptProfileId)?.name ?? null)
    : null;

  const crmPipelineMatch = crmPipelineId
    ? crmStagesReference.find((p) => p.pipelineId === crmPipelineId)
    : undefined;
  const crmStageMatch = crmPipelineMatch?.stages.find((s) => s.stageId === crmStageId);

  return {
    promptProfileName,
    crmStageName: crmStageMatch?.name ?? null,
    crmPipelineName: crmPipelineMatch?.name ?? null,
    crmMultiplePipelines: crmStagesReference.length > 1,
  };
}
